import { Box, BoxProps, Stack, useColorMode, useColorModeValue } from '@chakra-ui/react';
import { Global } from '@emotion/react';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { createEmptyHistoryState, HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { useStableCallback } from '@main/shared/browser';
import { EditorState, LexicalEditor } from 'lexical';
import { useMemo, useRef, useState } from 'react';

import AutoLinkPlugin from '../lexical/plugins/auto-link';
import { BlurEditorPlugin } from '../lexical/plugins/BlurEditorPlugin';
import { CodeHighlightPlugin } from '../lexical/plugins/code-highlight';
import { FloatingLinkEditorPlugin } from '../lexical/plugins/floating-link-editor';
import TableCellResizerPlugin from '../lexical/plugins/table-cell-resizer';
import { TableContextMenuPlugin } from '../lexical/plugins/table-context-menu';
import { ToolbarPlugin, ToolbarPluginProps } from '../lexical/plugins/toolbar';
import theme from '../lexical/themes/wysiwyg';
import styles from '../lexical/themes/wysiwyg/styles';
import { MD_SHORTCUT_TRANSFORMERS } from '../markdown/transformers';
import { getWysiwygNodes } from './nodes';
import {
  IMAGE_RESIZE_DRAFT_UPDATE_TAG,
  IMAGE_RESIZE_FINAL_UPDATE_TAG,
} from './nodes/editor-image-config';

export interface WysiwygEditorProps {
  boxProps?: BoxProps;
  toolbarProps?: ToolbarPluginProps;
  id?: string;
  placeholder?: string;
  config?: Partial<InitialConfigType>;
  enableImages?: boolean;
  onSubmit?(text: string): void;
  onStateChange?(editorState: EditorState, editor: LexicalEditor, tags: Set<string>): void;
  children?: React.ReactNode;
}

export function WysiwygEditor(props: WysiwygEditorProps) {
  return (
    <LexicalComposer
      initialConfig={{
        theme,
        namespace: `wysiwig-${props.id ?? 'editor'}`,
        // eslint-disable-next-line no-console
        onError: console.error,
        nodes: getWysiwygNodes({ images: props.enableImages }),
        ...props.config,
      }}
    >
      <Global styles={styles} />
      <WysiwygPlugins {...props} />
    </LexicalComposer>
  );
}

function WysiwygPlugins(props: WysiwygEditorProps) {
  const { colorMode } = useColorMode();
  const backgroundColor = useColorModeValue('white', 'gray.800');
  const historyState = useMemo(() => createEmptyHistoryState(), []);
  const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLElement>();
  const tiggerContextMenu = TableContextMenuPlugin.useTriggerContextMenu();
  const { onSubmit } = props;
  const hasChangedRef = useRef(false);
  const onStateChange = props.onStateChange;
  const handleOnBlur = useMemo(
    () =>
      onSubmit
        ? (editor: LexicalEditor) => {
            if (hasChangedRef.current) {
              onSubmit(JSON.stringify(editor.getEditorState()));
              hasChangedRef.current = false;
            }
          }
        : undefined,
    [onSubmit],
  );

  const handleEditorChange = useStableCallback(
    (editorState: EditorState, editor: LexicalEditor, tags: Set<string>) => {
      hasChangedRef.current = true;

      const isDraftImageResizeUpdate = tags.has(IMAGE_RESIZE_DRAFT_UPDATE_TAG);
      const isFinalImageResizeUpdate = tags.has(IMAGE_RESIZE_FINAL_UPDATE_TAG);

      if (isFinalImageResizeUpdate && onSubmit) {
        onSubmit(JSON.stringify(editor.getEditorState()));
        hasChangedRef.current = false;
      }

      // If editor is not focused but content changed (e.g., from toolbar plugins), then
      // submit the content immediately to ensure changes are saved.
      if (
        !isDraftImageResizeUpdate &&
        !isFinalImageResizeUpdate &&
        editor.getRootElement() !== document.activeElement &&
        onSubmit
      ) {
        onSubmit(JSON.stringify(editor.getEditorState()));
        hasChangedRef.current = false;
      }

      if (onStateChange) {
        onStateChange(editorState, editor, tags);
      }
    },
  );

  return (
    <Box
      position="relative"
      w="auto"
      minW="full"
      ref={(elem) => elem && setFloatingAnchorElem(elem)}
      {...props.boxProps}
    >
      <Stack
        minH="full"
        spacing="0"
        className={`editor-mode-${colorMode}`}
        background={backgroundColor}
        border="1px"
        borderColor={useColorModeValue('gray.200', 'gray.700')}
        borderRadius="xl"
      >
        <ToolbarPlugin
          enableImages={props.enableImages}
          borderTopRadius="xl"
          flexShrink="0"
          px="0"
          pt="4"
          pb="4"
          {...props.toolbarProps}
        />
        <Box
          className="editor-container"
          p="3"
          minH="full"
          flexGrow="1"
          display="flex"
          flexDir="column"
        >
          <RichTextPlugin
            placeholder={<div className="editor-placeholder">{props.placeholder}</div>}
            contentEditable={
              <ContentEditable
                style={{
                  flexGrow: '1',
                  overflowX: 'auto',
                  minHeight: '100%',
                  minWidth: '100%',
                  maxWidth: '100%',
                }}
                className="editor-input"
                onContextMenu={tiggerContextMenu}
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </Box>
      </Stack>
      <HistoryPlugin externalHistoryState={historyState} />
      <CodeHighlightPlugin />
      <ListPlugin />
      <CheckListPlugin />
      <MarkdownShortcutPlugin transformers={MD_SHORTCUT_TRANSFORMERS} />
      <TabIndentationPlugin />
      <LinkPlugin />
      <AutoLinkPlugin />
      <FloatingLinkEditorPlugin anchorElem={floatingAnchorElem} />
      <TablePlugin hasCellMerge hasCellBackgroundColor />
      <TableContextMenuPlugin />
      <TableCellResizerPlugin />
      {handleOnBlur ? <BlurEditorPlugin onBlur={handleOnBlur} /> : ''}
      <OnChangePlugin onChange={handleEditorChange} ignoreSelectionChange />
      {props.children}
    </Box>
  );
}
